/**
 * O vocabulário dos agentes: tons, ferramentas e o que cada limite significa.
 *
 * Fica fora dos adaptadores porque é a mesma lista na tela e no prompt — e
 * porque é aqui que se lê, de uma vez, tudo o que um agente pode fazer.
 */

import type { TomDoAgente } from './tipos';

export const TONS: { id: TomDoAgente; rotulo: string; descricao: string }[] = [
    { id: 'acolhedor', rotulo: 'Acolhedor', descricao: 'Gentil e próximo, sem formalidade excessiva' },
    { id: 'direto', rotulo: 'Direto', descricao: 'Frases curtas, começando pela conclusão' },
    { id: 'tecnico', rotulo: 'Técnico', descricao: 'Preciso, com os números à frente da opinião' },
    { id: 'formal', rotulo: 'Formal', descricao: 'Tratamento respeitoso e impessoal' },
    { id: 'descontraido', rotulo: 'Descontraído', descricao: 'Leve e conversado, sem gírias' },
];

export function tomPorId(id: string) {
    return TONS.find((t) => t.id === id);
}

/**
 * O que a temperatura muda, em palavras de quem vai escolher.
 *
 * "0.7" não diz nada a quem administra uma clínica; "responde igual todo dia"
 * diz.
 */
export function descreverTemperatura(valor: number): string {
    if (valor <= 0.2) return 'Responde quase igual todo dia — bom para agenda e dinheiro';
    if (valor <= 0.5) return 'Equilibrado: varia o texto, mantém a conclusão';
    if (valor <= 0.8) return 'Mais criativo: bom para texto de campanha, arriscado para número';
    return 'Muito criativo: evite quando a resposta precisar ser sempre a mesma';
}

export type CategoriaDeFerramenta = 'agenda' | 'paciente' | 'relacionamento' | 'catalogo';

export interface Ferramenta {
    id: string;
    nome: string;
    categoria: CategoriaDeFerramenta;
    descricao: string;
    /**
     * Ferramenta que ESCREVE não executa sozinha: ela devolve uma proposta que
     * alguém confirma. É a regra do produto — "a ação da IA só executa após
     * confirmação explícita, com os campos exatos à vista".
     */
    escreve: boolean;
    /** O que ela precisa saber para agir. */
    parametros: { nome: string; descricao: string; obrigatorio: boolean }[];
}

export const FERRAMENTAS: Ferramenta[] = [
    {
        id: 'agenda_horarios_livres',
        nome: 'Ver horários livres',
        categoria: 'agenda',
        descricao: 'Lê a jornada da clínica e a agenda do período, e devolve os encaixes possíveis.',
        escreve: false,
        parametros: [
            { nome: 'de', descricao: 'Data inicial (YYYY-MM-DD)', obrigatorio: true },
            { nome: 'ate', descricao: 'Data final (YYYY-MM-DD)', obrigatorio: true },
            { nome: 'duracao_min', descricao: 'Duração desejada, em minutos', obrigatorio: false },
        ],
    },
    {
        id: 'agenda_consultar_paciente',
        nome: 'Ver consultas de um paciente',
        categoria: 'agenda',
        descricao: 'Últimas consultas e a próxima marcada, se houver.',
        escreve: false,
        parametros: [{ nome: 'nome', descricao: 'Nome do paciente', obrigatorio: true }],
    },
    {
        id: 'agenda_agendar',
        nome: 'Agendar consulta',
        categoria: 'agenda',
        descricao: 'Propõe um agendamento com data, hora, procedimento e profissional.',
        escreve: true,
        parametros: [
            { nome: 'paciente', descricao: 'Nome do paciente', obrigatorio: true },
            { nome: 'data', descricao: 'YYYY-MM-DD', obrigatorio: true },
            { nome: 'hora', descricao: 'HH:MM', obrigatorio: true },
            { nome: 'procedimento', descricao: 'Nome do procedimento', obrigatorio: true },
            { nome: 'profissional', descricao: 'Nome do profissional', obrigatorio: false },
        ],
    },
    {
        id: 'paciente_buscar',
        nome: 'Buscar paciente',
        categoria: 'paciente',
        descricao: 'Encontra a pessoa pelo nome ou telefone e devolve o cadastro básico.',
        escreve: false,
        parametros: [{ nome: 'busca', descricao: 'Nome ou telefone', obrigatorio: true }],
    },
    {
        id: 'paciente_sem_retorno',
        nome: 'Listar quem não voltou',
        categoria: 'relacionamento',
        descricao: 'Pacientes atendidos há mais de N dias e sem nada marcado.',
        escreve: false,
        parametros: [{ nome: 'dias', descricao: 'Dias sem retorno (padrão 90)', obrigatorio: false }],
    },
    {
        id: 'followup_criar',
        nome: 'Criar follow-up',
        categoria: 'relacionamento',
        descricao: 'Propõe uma tarefa para a equipe, com título, responsável e prazo.',
        escreve: true,
        parametros: [
            { nome: 'titulo', descricao: 'O que precisa ser feito', obrigatorio: true },
            { nome: 'vence_em', descricao: 'YYYY-MM-DD', obrigatorio: false },
            { nome: 'paciente', descricao: 'Paciente relacionado', obrigatorio: false },
        ],
    },
    {
        id: 'regua_criar',
        nome: 'Criar régua de relacionamento',
        categoria: 'relacionamento',
        descricao: 'Propõe um lembrete na régua do agendamento: quando, por qual canal e com que texto.',
        escreve: true,
        parametros: [
            { nome: 'quando', descricao: 'Rótulo: “1 dia antes”', obrigatorio: true },
            { nome: 'offset_min', descricao: 'Minutos antes do atendimento', obrigatorio: true },
            { nome: 'descricao', descricao: 'Texto da mensagem', obrigatorio: true },
            { nome: 'canal', descricao: 'whatsapp, email ou ambos', obrigatorio: false },
        ],
    },
    {
        id: 'catalogo_procedimentos',
        nome: 'Consultar procedimentos',
        categoria: 'catalogo',
        descricao: 'Nome, duração e preço do que a clínica faz.',
        escreve: false,
        parametros: [{ nome: 'busca', descricao: 'Filtro por nome', obrigatorio: false }],
    },
];

export function ferramentaPorId(id: string): Ferramenta | undefined {
    return FERRAMENTAS.find((f) => f.id === id);
}

export const CATEGORIAS_DE_FERRAMENTA: { id: CategoriaDeFerramenta; rotulo: string }[] = [
    { id: 'agenda', rotulo: 'Agenda' },
    { id: 'paciente', rotulo: 'Pacientes' },
    { id: 'relacionamento', rotulo: 'Relacionamento' },
    { id: 'catalogo', rotulo: 'Catálogo' },
];
